// ============================================================================
// PROTECTED ROUTE — Route-level authentication + module access guard
// ============================================================================
// Wraps routes that require authentication and optionally a specific module.

import React, { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';

interface ProtectedRouteProps {
  children: ReactNode;
  /** Require access to a specific module */
  moduleCode?: string;
  /** Redirect target when not authenticated (default: '/') */
  redirectTo?: string;
  /** Loading component to show while checking auth */
  loadingComponent?: ReactNode;
}

const getAuthMode = (): 'tenant-hub' | 'legacy' => {
  // Fail-secure: ONLY an explicit REACT_APP_AUTH_MODE=legacy disables the route
  // guard. Unset/unknown values resolve to tenant-hub so protection stays on.
  return process.env.REACT_APP_AUTH_MODE === 'legacy' ? 'legacy' : 'tenant-hub';
};

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  moduleCode,
  redirectTo = '/',
  loadingComponent,
}) => {
  const { isAuthenticated, isLoading, permissionEngine } = useAuthContext();

  // Legacy mode: passthrough — no route-level auth guard (preserves pre-RBAC behavior)
  if (getAuthMode() !== 'tenant-hub') {
    return <>{children}</>;
  }

  if (isLoading) {
    return <>{loadingComponent ?? <div>Loading...</div>}</>;
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  if (moduleCode && !permissionEngine.hasModuleAccess(moduleCode)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
